import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDevice } from '@/store/device';
import { deviceFsApi, filesApi, localFsApi, serverInfoApi, transferApi } from '@/lib/api';
import type { DeviceFileKind, DeviceFsEntry, FileMeta } from '@/lib/types';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  ChevronUp,
  Download,
  Eye,
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileText,
  Film,
  ExternalLink,
  Folder,
  FolderPlus,
  Image as ImageIcon,
  LayoutGrid,
  LayoutList,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  RotateCcw,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { formatBytes } from '@/lib/format';
import { FilePreviewModal, type PreviewSource } from '@/components/FilePreviewModal';

/**
 * Pseudo-kind for the "全部" tab. Backed by a single API call that
 * omits the `kind` query param, letting the server return every
 * DeviceFileKind in one listing. The 4 real kinds keep their dedicated
 * per-kind tabs for users who want the focused view.
 */
type PcTabKind = DeviceFileKind | 'all';

const PC_KIND_LABEL: Record<PcTabKind, string> = {
  all: '全部',
  screenshots: '截图',
  recordings: '录像',
  logcat: '日志',
  apks: '安装包',
};

/**
 * Tailwind classes for the per-kind type badge used in the flat table.
 * Each kind gets its own hue so the row's category is identifiable
 * at a glance without reading the file extension.
 */
const KIND_BADGE: Record<DeviceFileKind, string> = {
  screenshots: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  recordings: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
  logcat: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
  apks: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

const PREVIEWABLE_EXT = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp',
  'mp4', 'webm', 'mov', 'm4v',
  'txt', 'log', 'json', 'xml', 'yml', 'yaml', 'md', 'csv', 'ini',
]);

function isPreviewableName(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return PREVIEWABLE_EXT.has(ext);
}

/**
 * Render `<homeDir>/foo` as `~/foo` for display. Pure cosmetic — backend
 * always stores absolute paths.
 */
function humanisePath(p: string, homeDir?: string): string {
  if (!homeDir) return p;
  if (p === homeDir) return '~';
  if (p.startsWith(homeDir + '/') || p.startsWith(homeDir + '\\')) {
    return '~' + p.slice(homeDir.length);
  }
  return p;
}

/**
 * Which side of the dual-pane layout the user has folded away. When
 * folded, the pane shows as a thin vertical rail with a chevron to
 * un-fold; the other pane expands to the full content width. This
 * unlocks the table to render every column without truncation on
 * laptop / narrower screens where the default 50/50 split squeezes
 * the "全部" view's filename + mtime columns.
 *
 *   - 'none'   → 50 / 50
 *   - 'device' → device pane folded, PC pane full width
 *   - 'pc'     → PC pane folded, device pane full width
 */
type PaneFold = 'none' | 'device' | 'pc';

export default function Files() {
  const { serial, isOnline } = useDevice();
  const queryClient = useQueryClient();
  const [devicePath, setDevicePath] = useState('/sdcard/');
  // Default to the new "全部" view so the user lands on the union of
  // every kind. Per-kind tabs are still one click away.
  const [pcKind, setPcKind] = useState<PcTabKind>('all');
  const [transfers, setTransfers] = useState<TransferTask[]>([]);
  const [preview, setPreview] = useState<{ source: PreviewSource; caption: string } | undefined>();
  const [paneFold, setPaneFold] = useState<PaneFold>('none');

  /**
   * Invalidate AND immediately re-fetch every cached `['files', serial, *]`
   * listing — active and inactive — so a delete / transfer / cleanup on
   * one tab is reflected the next time the user navigates to a sibling
   * tab. Previously we only called `pc.refetch()` which refreshes the
   * active query alone; the next sibling tab visit returned stale
   * React Query cache. Using `refetchType: 'all'` forces inactive
   * queries to re-run too, so the "全部" tab is fresh the instant the
   * user clicks it (eliminating the "刷新慢" perception).
   */
  const refreshAllPcKinds = (): void => {
    if (!serial) return;
    void queryClient.invalidateQueries({
      queryKey: ['files', serial],
      refetchType: 'all',
    });
  };

  /**
   * Optimistically yank the deleted paths out of every cached
   * `['files', serial, *]` listing **before** the network round-trip
   * completes. This is the "feels instant" half of the delete fix:
   * the user sees the rows disappear from the active tab the moment
   * they confirm, and switching to a sibling tab also shows the
   * pruned list (the background refetch then reconciles with the
   * server's authoritative view).
   */
  const removeFromAllPcCaches = (paths: string[]): void => {
    if (!serial || paths.length === 0) return;
    const dead = new Set(paths);
    queryClient.setQueriesData<{ items: FileMeta[] }>(
      { queryKey: ['files', serial] },
      (old) => (old ? { ...old, items: old.items.filter((f) => !dead.has(f.path)) } : old)
    );
  };

  const dev = useQuery({
    queryKey: ['devicefs', serial, devicePath],
    queryFn: () => deviceFsApi.list(serial!, devicePath),
    enabled: !!serial && isOnline,
  });
  // `kind: undefined` lets the server iterate every DeviceFileKind in
  // DEVICE_FILE_KINDS — the storage layer already handles that branch
  // (src/server/storage.ts) so the 全部 tab is a single round trip.
  const pc = useQuery({
    queryKey: ['files', serial, pcKind],
    queryFn: () =>
      filesApi.list({
        serial: serial!,
        kind: pcKind === 'all' ? undefined : pcKind,
      }),
    enabled: !!serial,
  });
  const serverInfo = useQuery({
    queryKey: ['server-info'],
    queryFn: () => serverInfoApi.get(),
    staleTime: 60_000,
  });

  // The Topbar widget broadcasts when the user picks a new devices directory.
  // Re-fetch the listing AND server-info so the displayed root + file list
  // both immediately reflect the new path (the listing is empty until the
  // first capture lands there, which is the correct UX).
  useEffect(() => {
    const onChanged = () => {
      void serverInfo.refetch();
      refreshAllPcKinds();
    };
    window.addEventListener('adh:devices-dir-changed', onChanged as EventListener);
    return () => window.removeEventListener('adh:devices-dir-changed', onChanged as EventListener);
    // refreshAllPcKinds is closure-stable on (queryClient, serial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverInfo, serial]);

  // Device file preview streams the bytes via `adb exec-out cat` straight
  // to the browser — no devices-dir copy until the user explicitly clicks
  // download or the transfer button. (Was previously auto-pulling, which
  // surprised users by polluting devices with every preview.)
  const previewDeviceEntry = (entry: DeviceFsEntry) => {
    if (!serial) return;
    const dp = devicePath.replace(/\/?$/, '/') + entry.name;
    setPreview({
      source: {
        kind: 'device',
        serial,
        devicePath: dp,
        name: entry.name,
        size: entry.size,
      },
      caption: '设备直读',
    });
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold tracking-tight">文件管理</h1>
        <p className="text-xs text-ink-muted">
          双面板对照：左 = 手机 sdcard，右 = PC 本地存档。可拖拽传输或点击文件名预览。
          {serverInfo.data && (
            <>
              {' '}
              存放目录：
              <span className="font-mono text-ink">
                {humanisePath(serverInfo.data.devicesRoot, serverInfo.data.homeDir)}
              </span>
            </>
          )}
        </p>
      </header>
      {!isOnline && (
        <div className="card-pad border-amber-500 text-xs text-amber-600">
          手机已离线，sdcard 浏览暂不可用，但仍可查看 PC 端已抓取的文件。
        </div>
      )}
      <div
        className={clsx(
          'grid gap-4',
          paneFold === 'none' && 'grid-cols-1 lg:grid-cols-2',
          paneFold === 'device' && 'grid-cols-[40px_minmax(0,1fr)]',
          paneFold === 'pc' && 'grid-cols-[minmax(0,1fr)_40px]'
        )}
      >
        {paneFold === 'device' ? (
          <FoldedRail
            label="手机"
            onExpand={() => setPaneFold('none')}
            side="left"
          />
        ) : (
          <DevicePane
            serial={serial}
            isOnline={isOnline}
            path={devicePath}
            setPath={setDevicePath}
            entries={dev.data?.entries ?? []}
            loading={dev.isLoading}
            error={(dev.error as Error | undefined)?.message}
            refetch={() => dev.refetch()}
            folded={false}
            onFold={() => setPaneFold('device')}
            siblingFolded={paneFold === 'pc'}
            onUnfoldSibling={() => setPaneFold('none')}
            onPullEntry={(entry) => {
              const dp = devicePath.replace(/\/?$/, '/') + entry.name;
              const id = pushTask(setTransfers, { kind: 'pull', name: entry.name, total: entry.size });
              transferApi
                .pull(serial!, dp)
                .then((res) => completeTask(setTransfers, id, `→ ${res.meta.path}`, res.meta.path))
                .catch((e) => failTask(setTransfers, id, (e as Error).message))
                .finally(() => refreshAllPcKinds());
            }}
            onPreviewEntry={previewDeviceEntry}
            onUploadFile={(file) => {
              const target = devicePath.replace(/\/?$/, '/') + file.name;
              const id = pushTask(setTransfers, { kind: 'push', name: file.name, total: file.size });
              transferApi
                .push(serial!, target, file)
                .then((res) => completeTask(setTransfers, id, `${res.result.bytes}B → ${res.result.remote}`))
                .catch((e) => failTask(setTransfers, id, (e as Error).message))
                .finally(() => dev.refetch());
            }}
          />
        )}
        {paneFold === 'pc' ? (
          <FoldedRail
            label="PC 本地存档"
            onExpand={() => setPaneFold('none')}
            side="right"
          />
        ) : (
          <PcPane
            serial={serial}
            kind={pcKind}
            setKind={setPcKind}
            items={pc.data?.items ?? []}
            loading={pc.isLoading}
            refetch={refreshAllPcKinds}
            folded={false}
            onFold={() => setPaneFold('pc')}
            siblingFolded={paneFold === 'device'}
            onUnfoldSibling={() => setPaneFold('none')}
            devicesRoot={serverInfo.data?.devicesRoot}
            homeDir={serverInfo.data?.homeDir}
            onPushFile={(file) => {
              if (!isOnline) {
                alert('手机已离线，无法推送到设备。');
                return;
              }
              const target = devicePath.replace(/\/?$/, '/') + file.name;
              const id = pushTask(setTransfers, { kind: 'push', name: file.name, total: file.bytes });
              fetch(filesApi.rawUrl(file.path))
                .then((r) => r.blob())
                .then((blob) => transferApi.push(serial!, target, blob))
                .then((res) => completeTask(setTransfers, id, `→ ${res.result.remote}`))
                .catch((e) => failTask(setTransfers, id, (e as Error).message))
                .finally(() => dev.refetch());
            }}
            onPreview={(meta) => setPreview({ source: { kind: 'pc', meta }, caption: 'PC' })}
            onDelete={async (paths) => {
              if (!confirm(`将删除 ${paths.length} 个文件，确定继续？`)) return;
              // Optimistic: drop the rows from every cached PC kind so
              // the UI updates in the same frame the user clicks 删除.
              removeFromAllPcCaches(paths);
              try {
                await filesApi.delete(paths);
              } catch (e) {
                alert(`删除失败：${(e as Error).message}`);
              } finally {
                // Re-sync with the server's authoritative listing
                // (and pick up anything the optimistic update missed).
                refreshAllPcKinds();
              }
            }}
            canPushToDevice={isOnline}
          />
        )}
      </div>
      {transfers.length > 0 && (
        <div className="card-pad">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-medium">传输记录</h3>
            <button className="btn-ghost" onClick={() => setTransfers([])}>
              清空
            </button>
          </div>
          <ul className="space-y-1">
            {transfers.map((t) => (
              <li
                key={t.id}
                className={clsx(
                  'flex items-center justify-between gap-2 rounded border border-line px-2 py-1 text-xs',
                  t.status === 'failed' && 'border-rose-500 text-rose-500',
                  t.status === 'done' && 'text-ink-muted'
                )}
              >
                <span className="font-mono">
                  {t.kind === 'pull' ? <Download className="-mt-0.5 mr-1 inline h-3 w-3" /> : <Upload className="-mt-0.5 mr-1 inline h-3 w-3" />}
                  {t.name}
                </span>
                <span className="flex-1 text-right">
                  {t.status === 'running'
                    ? '传输中…'
                    : t.status === 'failed'
                      ? `失败：${t.message}`
                      : t.message ?? '完成'}
                </span>
                {t.status === 'done' && t.revealPath && (
                  <button
                    type="button"
                    className="btn-ghost text-[11px]"
                    title={`在文件管理器中显示 ${t.revealPath}`}
                    onClick={() => void revealInOsFileManager(t.revealPath!)}
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>显示文件</span>
                  </button>
                )}
                <span className="tabular-nums text-ink-muted">{formatBytes(t.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {preview && (
        <FilePreviewModal
          source={preview.source}
          caption={preview.caption}
          serial={serial}
          isOnline={isOnline}
          onClose={() => setPreview(undefined)}
        />
      )}
    </div>
  );
}

interface TransferTask {
  id: string;
  kind: 'pull' | 'push';
  name: string;
  total?: number;
  status: 'running' | 'done' | 'failed';
  message?: string;
  /** Absolute PC path of the artifact for pulls — clicking 「显示文件」 reveals it. */
  revealPath?: string;
}

function pushTask(setter: (fn: (prev: TransferTask[]) => TransferTask[]) => void, t: Omit<TransferTask, 'id' | 'status'>): string {
  const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  setter((prev) => [...prev, { ...t, id, status: 'running' }]);
  return id;
}

function completeTask(
  setter: (fn: (prev: TransferTask[]) => TransferTask[]) => void,
  id: string,
  message: string,
  revealPath?: string,
): void {
  setter((prev) =>
    prev.map((t) => (t.id === id ? { ...t, status: 'done', message, revealPath } : t)),
  );
}
function failTask(setter: (fn: (prev: TransferTask[]) => TransferTask[]) => void, id: string, message: string): void {
  setter((prev) => prev.map((t) => (t.id === id ? { ...t, status: 'failed', message } : t)));
}

/**
 * Pop the OS file manager focused on `path`. Failures only ever
 * surface as a non-fatal `alert` — revealing is best-effort, the file
 * is already on disk regardless of whether the GUI opens.
 */
async function revealInOsFileManager(path: string): Promise<void> {
  try {
    const result = await localFsApi.reveal(path);
    if (result.status === 'error') {
      window.alert(`无法打开文件管理器：${result.reason}`);
    } else if (result.status === 'unsupported') {
      window.alert(`本平台未支持「在文件管理器中显示」：${result.reason}`);
    }
  } catch (e) {
    window.alert(`无法打开文件管理器：${(e as Error).message}`);
  }
}

/* -------------------------------------------------------------------- */
/* Folded-pane rail                                                     */
/* -------------------------------------------------------------------- */

/**
 * Thin vertical rail rendered in place of a folded pane. The whole
 * rail is one big clickable surface, with a `PanelLeftOpen` /
 * `PanelRightOpen` icon at the top + the pane label rotated 90° in
 * the middle. Clicking anywhere on the rail un-folds the pane —
 * previously we only had a chevron icon at the very top, which the
 * user reported as "看不出来需要折叠".
 */
function FoldedRail({
  label,
  side,
  onExpand,
}: {
  label: string;
  side: 'left' | 'right';
  onExpand: () => void;
}) {
  const Icon = side === 'left' ? PanelLeftOpen : PanelRightOpen;
  return (
    <button
      type="button"
      className="card group flex w-[40px] flex-col items-center justify-between overflow-hidden py-3 text-ink-muted transition hover:bg-bg-soft hover:text-accent"
      title={`展开「${label}」`}
      onClick={onExpand}
    >
      <Icon className="h-5 w-5" />
      <div
        className="origin-center select-none whitespace-nowrap text-[11px] font-medium"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        {label}
      </div>
      <span className="rounded-full bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent opacity-0 transition group-hover:opacity-100">
        展开
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------- */
/* Device pane                                                          */
/* -------------------------------------------------------------------- */

/* -------------------------------------------------------------------- */
/* Device pane: quick-jump roots, category filter, thumbnail grid       */
/* -------------------------------------------------------------------- */

/**
 * High-traffic destinations on basically every Android device. Surfacing
 * them as chips makes "open camera roll" or "open downloads" a single
 * click instead of typing `/sdcard/DCIM/`. The leading `/sdcard` chip
 * doubles as a "home" button when the user wandered into a sibling
 * tree (e.g. `/storage/emulated/0/Android/data/`).
 */
const QUICK_PATHS: { label: string; path: string }[] = [
  { label: '/sdcard', path: '/sdcard/' },
  { label: 'DCIM', path: '/sdcard/DCIM/' },
  { label: 'Pictures', path: '/sdcard/Pictures/' },
  { label: 'Movies', path: '/sdcard/Movies/' },
  { label: 'Download', path: '/sdcard/Download/' },
  { label: 'Music', path: '/sdcard/Music/' },
  { label: 'Documents', path: '/sdcard/Documents/' },
  { label: 'Android', path: '/sdcard/Android/' },
];

type CategoryKind = 'all' | 'image' | 'video' | 'audio' | 'doc' | 'apk' | 'archive' | 'other';

const CATEGORY_DEFS: { kind: CategoryKind; label: string; exts: ReadonlySet<string> }[] = [
  { kind: 'all', label: '全部', exts: new Set() },
  {
    kind: 'image',
    label: '图片',
    exts: new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'avif']),
  },
  {
    kind: 'video',
    label: '视频',
    exts: new Set(['mp4', 'webm', 'mov', 'm4v', 'mkv', '3gp']),
  },
  {
    kind: 'audio',
    label: '音频',
    exts: new Set(['mp3', 'wav', 'flac', 'm4a', 'ogg', 'aac', 'opus']),
  },
  {
    kind: 'doc',
    label: '文档',
    exts: new Set([
      'txt', 'log', 'json', 'xml', 'yml', 'yaml', 'md', 'csv', 'ini',
      'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'html', 'htm',
    ]),
  },
  { kind: 'apk', label: '安装包', exts: new Set(['apk', 'aab', 'xapk', 'apks']) },
  {
    kind: 'archive',
    label: '压缩包',
    exts: new Set(['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz', 'tbz', 'tbz2']),
  },
  { kind: 'other', label: '其他', exts: new Set() },
];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function categoryOf(name: string): CategoryKind {
  const e = extOf(name);
  if (!e) return 'other';
  for (const def of CATEGORY_DEFS) {
    if (def.kind === 'all' || def.kind === 'other') continue;
    if (def.exts.has(e)) return def.kind;
  }
  return 'other';
}

const IMAGE_EXTS = CATEGORY_DEFS.find((d) => d.kind === 'image')!.exts;
const VIDEO_EXTS = CATEGORY_DEFS.find((d) => d.kind === 'video')!.exts;

function isImageName(name: string): boolean {
  return IMAGE_EXTS.has(extOf(name));
}
function isVideoName(name: string): boolean {
  return VIDEO_EXTS.has(extOf(name));
}

/**
 * Split a unix path into its display segments. Roots ("/" or "") return
 * an empty list — Breadcrumb renders the leading slash itself.
 */
function splitPathSegments(p: string): string[] {
  return p.split('/').filter(Boolean);
}

interface DevicePaneProps {
  serial: string | undefined;
  isOnline: boolean;
  path: string;
  setPath: (p: string) => void;
  entries: DeviceFsEntry[];
  loading: boolean;
  error?: string;
  refetch: () => void;
  onPullEntry: (entry: DeviceFsEntry) => void;
  onPreviewEntry: (entry: DeviceFsEntry) => void;
  onUploadFile: (file: File) => void;
  /**
   * Fold controls. `folded` is currently always `false` (we render the
   * `FoldedRail` instead when folded), but the prop is kept so the
   * caller's mental model — "every pane knows its fold state" — stays
   * symmetric and self-documenting. `onFold` collapses *this* pane.
   * `siblingFolded` / `onUnfoldSibling` let the un-folded pane offer a
   * one-click "show the other side again" gesture.
   */
  folded?: boolean;
  onFold?: () => void;
  siblingFolded?: boolean;
  onUnfoldSibling?: () => void;
}

function DevicePane({
  serial,
  isOnline,
  path,
  setPath,
  entries,
  loading,
  error,
  refetch,
  onPullEntry,
  onPreviewEntry,
  onUploadFile,
  onFold,
  siblingFolded,
  onUnfoldSibling,
}: DevicePaneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  // Local UI state — all reset to defaults on directory change so the
  // user lands in a clean view every time they navigate. (Editing the
  // path string itself doesn't reset, mirroring how Finder / Files by
  // Google handle the address bar.)
  const [editingPath, setEditingPath] = useState(false);
  const [pathDraft, setPathDraft] = useState(path);
  const [category, setCategory] = useState<CategoryKind>('all');
  const [query, setQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const enabled = Boolean(serial) && isOnline;

  // Path changes are the canonical "fresh view" trigger — clear any
  // selection (a list of file names that no longer exist in this dir)
  // and reset the search box so users don't get hidden empty states.
  useEffect(() => {
    setSelected(new Set());
    setQuery('');
    setPathDraft(path);
    setEditingPath(false);
  }, [path]);

  const goUp = () => {
    const trimmed = path.replace(/\/+$/, '');
    if (!trimmed || trimmed === '/sdcard') return;
    const parent = trimmed.slice(0, trimmed.lastIndexOf('/')) || '/';
    setPath(parent.endsWith('/') ? parent : `${parent}/`);
  };

  // Apply category + search filter. Directories are always shown so
  // navigation isn't blocked when the user picks "图片" but the parent
  // dir contains sub-folders.
  const visibleEntries = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      const isDir = e.type === 'dir' || e.type === 'symlink';
      if (q && !e.name.toLowerCase().includes(q)) return false;
      if (category === 'all' || isDir) return true;
      return categoryOf(e.name) === category;
    });
  }, [entries, category, query]);

  // File-only counts power the filter status bar. Directories are
  // excluded because they're never filtered out by the category /
  // search anyway — counting them would mask the actually-filtered
  // file population (e.g. "12 / 47 文件" is what the user cares about
  // when they click 图片, not "12 / 47+5dir").
  const totalFileCount = useMemo(
    () => entries.filter((e) => e.type === 'file').length,
    [entries]
  );
  const visibleFileCount = useMemo(
    () => visibleEntries.filter((e) => e.type === 'file').length,
    [visibleEntries]
  );
  const filterActive = category !== 'all' || query.trim().length > 0;

  const selectableNames = useMemo(
    () => visibleEntries.filter((e) => e.type !== 'dir').map((e) => e.name),
    [visibleEntries]
  );
  const allSelectableSelected =
    selectableNames.length > 0 && selectableNames.every((n) => selected.has(n));

  const toggleSelected = (name: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const clearSelection = (): void => setSelected(new Set());
  const toggleSelectAll = (): void => {
    if (allSelectableSelected) clearSelection();
    else setSelected(new Set(selectableNames));
  };

  const fullPathOf = (name: string): string =>
    path.replace(/\/?$/, '/') + name;

  const bulkPull = (): void => {
    for (const name of selected) {
      const entry = entries.find((e) => e.name === name);
      if (!entry) continue;
      onPullEntry(entry);
    }
    clearSelection();
  };

  const bulkRemove = async (): Promise<void> => {
    if (!serial || selected.size === 0) return;
    if (!confirm(`将删除 ${selected.size} 个项目，确定继续？`)) return;
    // Sequential removal keeps the device-side adb shell pool from
    // queuing N concurrent `rm` invocations; the user is already
    // waiting for "delete 50 files" so latency parallelism isn't
    // worth the unbounded fan-out.
    for (const name of selected) {
      const entry = entries.find((e) => e.name === name);
      const isDir = entry?.type === 'dir' || entry?.type === 'symlink';
      try {
        await deviceFsApi.remove(serial, fullPathOf(name), isDir);
      } catch (e) {
        alert(`删除 ${name} 失败：${(e as Error).message}`);
      }
    }
    clearSelection();
    refetch();
  };

  const commitPathDraft = (): void => {
    const next = pathDraft.trim() || '/sdcard/';
    setPath(next.endsWith('/') ? next : `${next}/`);
    setEditingPath(false);
  };

  return (
    <section
      className={clsx('card flex flex-col overflow-hidden', dragOver && 'ring-2 ring-accent')}
      onDragOver={(e) => {
        e.preventDefault();
        if (enabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!enabled) return;
        for (const file of Array.from(e.dataTransfer.files)) onUploadFile(file);
      }}
    >
      {/* Header row 1: nav + path + global actions ------------------- */}
      <header className="flex items-center justify-between gap-2 border-b border-line bg-bg-soft px-3 py-2 text-sm">
        <div className="flex items-center gap-1">
          <button className="btn-ghost" onClick={goUp} disabled={!enabled} title="上一级">
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <span className="text-xs text-ink-muted">手机</span>
          {siblingFolded && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[11px] text-accent hover:bg-accent/20"
              onClick={onUnfoldSibling}
              title="展开右侧 PC 本地存档"
            >
              <PanelRightOpen className="h-3.5 w-3.5" />
              <span>展开右侧</span>
            </button>
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2 px-2">
          <Folder className="h-4 w-4 shrink-0 text-ink-dim" />
          {editingPath ? (
            <input
              className="input w-full font-mono text-xs"
              value={pathDraft}
              onChange={(e) => setPathDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitPathDraft();
                else if (e.key === 'Escape') {
                  setPathDraft(path);
                  setEditingPath(false);
                }
              }}
              onBlur={commitPathDraft}
              spellCheck={false}
              autoFocus
              disabled={!enabled}
            />
          ) : (
            <Breadcrumb
              path={path}
              onJump={(p) => enabled && setPath(p)}
              enabled={enabled}
            />
          )}
          <button
            type="button"
            className="btn-ghost shrink-0"
            onClick={() => {
              if (editingPath) commitPathDraft();
              else {
                setPathDraft(path);
                setEditingPath(true);
              }
            }}
            disabled={!enabled}
            title={editingPath ? '提交路径' : '直接编辑路径'}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="inline-flex items-center gap-1">
          <div className="mr-1 inline-flex overflow-hidden rounded-md border border-line">
            <button
              type="button"
              className={clsx(
                'inline-flex items-center px-1.5 py-1 text-xs',
                viewMode === 'list'
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:bg-bg-card'
              )}
              onClick={() => setViewMode('list')}
              title="列表视图"
              disabled={!enabled}
            >
              <LayoutList className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className={clsx(
                'inline-flex items-center px-1.5 py-1 text-xs',
                viewMode === 'grid'
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:bg-bg-card'
              )}
              onClick={() => setViewMode('grid')}
              title="缩略图网格"
              disabled={!enabled}
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            className="btn-ghost"
            disabled={!enabled}
            onClick={async () => {
              const name = prompt('新建目录名：');
              if (!name) return;
              await deviceFsApi.mkdir(serial!, fullPathOf(name));
              refetch();
            }}
            title="新建目录"
          >
            <FolderPlus className="h-3.5 w-3.5" />
          </button>
          <button
            className="btn-ghost"
            onClick={() => inputRef.current?.click()}
            disabled={!enabled}
            title="上传到设备"
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
          <input
            type="file"
            multiple
            ref={inputRef}
            className="hidden"
            onChange={(e) => {
              for (const f of Array.from(e.target.files ?? [])) onUploadFile(f);
              e.target.value = '';
            }}
          />
          <button className="btn-ghost" onClick={refetch} disabled={!enabled} title="刷新">
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          {onFold && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-1.5 py-0.5 text-[11px] text-ink-muted hover:border-accent/40 hover:bg-accent/10 hover:text-accent"
              onClick={onFold}
              title="折叠手机侧，让 PC 本地存档占满"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
              <span>折叠</span>
            </button>
          )}
        </div>
      </header>

      {/* Header row 2: quick-jump chips ----------------------------- */}
      <QuickChips path={path} setPath={setPath} enabled={enabled} />

      {/* Header row 3: category + search ---------------------------- */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-bg-card px-3 py-1.5">
        <CategoryChips active={category} setActive={setCategory} enabled={enabled} />
        <div className="ml-auto flex items-center gap-1 rounded-md border border-line bg-bg-soft px-2 py-1">
          <Search className="h-3.5 w-3.5 text-ink-dim" />
          <input
            type="search"
            placeholder="当前目录搜索"
            className="w-32 bg-transparent text-xs outline-none placeholder:text-ink-dim sm:w-44"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={!enabled}
          />
          {query && (
            <button
              type="button"
              className="text-ink-dim hover:text-ink"
              onClick={() => setQuery('')}
              title="清空搜索"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Header row 4: filter status bar (conditional) -------------- */}
      {filterActive && (
        <FilterStatus
          category={category}
          query={query}
          visibleFileCount={visibleFileCount}
          totalFileCount={totalFileCount}
          onClearCategory={() => setCategory('all')}
          onClearQuery={() => setQuery('')}
          onClearAll={() => {
            setCategory('all');
            setQuery('');
          }}
          jumpTo={setPath}
        />
      )}

      {/* Header row 5: bulk toolbar (conditional) ------------------- */}
      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          onClear={clearSelection}
          onPull={bulkPull}
          onRemove={bulkRemove}
        />
      )}

      {/* Body -------------------------------------------------------- */}
      <div className="min-h-0 flex-1 overflow-auto">
        {!enabled && (
          <p className="px-3 py-3 text-xs text-ink-muted">
            {!serial ? '请先选择一台设备。' : '设备离线，sdcard 暂不可用。'}
          </p>
        )}
        {enabled && loading && <p className="px-3 py-3 text-xs text-ink-muted">加载中…</p>}
        {error && <p className="px-3 py-3 text-xs text-rose-500">{error}</p>}
        {enabled && !loading && visibleEntries.length === 0 && (
          <p className="px-3 py-6 text-center text-xs text-ink-muted">
            {entries.length === 0
              ? '空目录'
              : '当前筛选条件下没有匹配项，可清空搜索或切换分类。'}
          </p>
        )}
        {enabled && visibleEntries.length > 0 && viewMode === 'list' && (
          <DeviceListView
            entries={visibleEntries}
            selected={selected}
            allSelected={allSelectableSelected}
            toggleSelected={toggleSelected}
            toggleSelectAll={toggleSelectAll}
            setPath={setPath}
            currentPath={path}
            onPreview={onPreviewEntry}
            onPull={onPullEntry}
            onDelete={async (entry) => {
              if (!serial) return;
              if (!confirm(`确定删除 ${entry.name}？`)) return;
              const isDir = entry.type === 'dir' || entry.type === 'symlink';
              await deviceFsApi.remove(serial, fullPathOf(entry.name), isDir);
              refetch();
            }}
          />
        )}
        {enabled && visibleEntries.length > 0 && viewMode === 'grid' && (
          <DeviceGridView
            serial={serial!}
            entries={visibleEntries}
            currentPath={path}
            selected={selected}
            toggleSelected={toggleSelected}
            setPath={setPath}
            onPreview={onPreviewEntry}
            onPull={onPullEntry}
            onDelete={async (entry) => {
              if (!serial) return;
              if (!confirm(`确定删除 ${entry.name}？`)) return;
              const isDir = entry.type === 'dir' || entry.type === 'symlink';
              await deviceFsApi.remove(serial, fullPathOf(entry.name), isDir);
              refetch();
            }}
          />
        )}
      </div>
      <footer className="border-t border-line bg-bg-soft px-3 py-1.5 text-[11px] text-ink-muted">
        将文件拖到这里推送到手机；点击行可进入目录或预览文件，勾选后顶部出现批处理工具。
      </footer>
    </section>
  );
}

/* ----------------- DevicePane subcomponents ------------------------- */

function Breadcrumb({
  path,
  onJump,
  enabled,
}: {
  path: string;
  onJump: (p: string) => void;
  enabled: boolean;
}) {
  const segs = splitPathSegments(path);
  return (
    <nav
      className="flex min-w-0 flex-1 items-center overflow-x-auto text-xs"
      aria-label="路径"
    >
      <button
        type="button"
        className={clsx(
          'shrink-0 px-1 text-ink-muted hover:text-accent',
          !enabled && 'cursor-not-allowed opacity-50'
        )}
        onClick={() => onJump('/')}
        disabled={!enabled}
        title="/"
      >
        /
      </button>
      {segs.map((seg, i) => {
        const target = '/' + segs.slice(0, i + 1).join('/') + '/';
        const isLast = i === segs.length - 1;
        return (
          <span key={target} className="inline-flex shrink-0 items-center">
            <button
              type="button"
              className={clsx(
                'truncate rounded px-1 py-0.5 font-mono',
                isLast
                  ? 'bg-accent/10 text-accent'
                  : 'text-ink hover:bg-bg-card hover:text-accent',
                !enabled && 'cursor-not-allowed opacity-50'
              )}
              onClick={() => onJump(target)}
              disabled={!enabled}
              title={target}
            >
              {seg}
            </button>
            {!isLast && <ChevronRight className="h-3 w-3 shrink-0 text-ink-dim" />}
          </span>
        );
      })}
    </nav>
  );
}

function QuickChips({
  path,
  setPath,
  enabled,
}: {
  path: string;
  setPath: (p: string) => void;
  enabled: boolean;
}) {
  // Normalise both sides so `/sdcard` vs `/sdcard/` doesn't break the
  // active-chip highlight.
  const norm = (p: string): string => p.replace(/\/+$/, '');
  const active = norm(path);
  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-line bg-bg-card px-3 py-1.5">
      <span className="shrink-0 text-[11px] text-ink-muted">快捷</span>
      {QUICK_PATHS.map((q) => {
        const isActive = active === norm(q.path);
        return (
          <button
            key={q.path}
            type="button"
            className={clsx(
              'shrink-0 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
              isActive
                ? 'border-accent/50 bg-accent/15 text-accent'
                : 'border-line bg-bg-soft text-ink-muted hover:border-accent/40 hover:text-accent',
              !enabled && 'cursor-not-allowed opacity-50'
            )}
            onClick={() => setPath(q.path)}
            disabled={!enabled}
            title={q.path}
          >
            {q.label}
          </button>
        );
      })}
    </div>
  );
}

function CategoryChips({
  active,
  setActive,
  enabled,
}: {
  active: CategoryKind;
  setActive: (k: CategoryKind) => void;
  enabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="shrink-0 text-[11px] text-ink-muted">类型</span>
      {CATEGORY_DEFS.map((def) => {
        const isActive = active === def.kind;
        return (
          <button
            key={def.kind}
            type="button"
            className={clsx(
              'shrink-0 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
              isActive
                ? 'border-accent/50 bg-accent/15 text-accent'
                : 'border-line bg-bg-soft text-ink-muted hover:border-accent/40 hover:text-accent',
              !enabled && 'cursor-not-allowed opacity-50'
            )}
            onClick={() => setActive(def.kind)}
            disabled={!enabled}
          >
            {def.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Visible explainer for "you're currently filtering" so the user
 * doesn't get confused when clicking a category chip on a
 * directory-heavy root (e.g. `/sdcard/` where 图片 keeps showing the
 * Pictures/DCIM/Movies subfolders because dirs are always visible).
 * Shows hit count vs. total, the active filters as removable tokens,
 * and — when nothing matched — suggests a couple of more-likely
 * destinations.
 */
function FilterStatus({
  category,
  query,
  visibleFileCount,
  totalFileCount,
  onClearCategory,
  onClearQuery,
  onClearAll,
  jumpTo,
}: {
  category: CategoryKind;
  query: string;
  visibleFileCount: number;
  totalFileCount: number;
  onClearCategory: () => void;
  onClearQuery: () => void;
  onClearAll: () => void;
  jumpTo: (p: string) => void;
}) {
  const trimmed = query.trim();
  const categoryLabel =
    CATEGORY_DEFS.find((d) => d.kind === category)?.label ?? '全部';
  const noHits = visibleFileCount === 0 && totalFileCount > 0;
  // Per-category suggestions for "click 图片 in /sdcard, nothing
  // matched, here's where the images actually live".
  const SUGGESTIONS: Partial<Record<CategoryKind, { label: string; path: string }[]>> = {
    image: [
      { label: 'DCIM', path: '/sdcard/DCIM/' },
      { label: 'Pictures', path: '/sdcard/Pictures/' },
    ],
    video: [
      { label: 'DCIM', path: '/sdcard/DCIM/' },
      { label: 'Movies', path: '/sdcard/Movies/' },
    ],
    audio: [
      { label: 'Music', path: '/sdcard/Music/' },
    ],
    doc: [
      { label: 'Documents', path: '/sdcard/Documents/' },
      { label: 'Download', path: '/sdcard/Download/' },
    ],
    apk: [{ label: 'Download', path: '/sdcard/Download/' }],
    archive: [{ label: 'Download', path: '/sdcard/Download/' }],
  };
  const suggestions = noHits && category !== 'all' ? SUGGESTIONS[category] : undefined;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-accent/30 bg-accent/5 px-3 py-1.5 text-xs">
      <span className="text-accent">筛选中</span>
      {category !== 'all' && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-accent/50 bg-bg-card px-2 py-0.5 text-accent hover:bg-accent/15"
          onClick={onClearCategory}
          title="清除类型筛选"
        >
          类型: {categoryLabel}
          <X className="h-3 w-3" />
        </button>
      )}
      {trimmed && (
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border border-accent/50 bg-bg-card px-2 py-0.5 text-accent hover:bg-accent/15"
          onClick={onClearQuery}
          title="清除搜索关键词"
        >
          关键词: {trimmed}
          <X className="h-3 w-3" />
        </button>
      )}
      <span className="tabular-nums text-ink-muted">
        命中 {visibleFileCount} / {totalFileCount} 文件
      </span>
      {suggestions && suggestions.length > 0 && (
        <span className="inline-flex items-center gap-1 text-ink-muted">
          · 试试
          {suggestions.map((s) => (
            <button
              key={s.path}
              type="button"
              className="rounded-full border border-line bg-bg-card px-1.5 py-0.5 text-accent hover:bg-accent/10"
              onClick={() => jumpTo(s.path)}
              title={s.path}
            >
              {s.label}
            </button>
          ))}
        </span>
      )}
      <button
        type="button"
        className="ml-auto inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-2 py-0.5 text-ink-muted hover:border-accent/40 hover:text-accent"
        onClick={onClearAll}
        title="清除所有筛选"
      >
        <X className="h-3 w-3" />
        清除筛选
      </button>
    </div>
  );
}

function BulkBar({
  count,
  onClear,
  onPull,
  onRemove,
}: {
  count: number;
  onClear: () => void;
  onPull: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-accent/30 bg-accent/10 px-3 py-1.5 text-xs">
      <span className="text-accent">已选中 {count} 项</span>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-bg-card px-2 py-0.5 text-accent hover:bg-accent/15"
        onClick={onPull}
      >
        <Download className="h-3 w-3" />
        批量拉到 PC
      </button>
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-bg-card px-2 py-0.5 text-rose-500 hover:bg-rose-500/10"
        onClick={onRemove}
      >
        <Trash2 className="h-3 w-3" />
        批量删除
      </button>
      <button
        type="button"
        className="ml-auto inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-2 py-0.5 text-ink-muted hover:border-accent/40 hover:text-accent"
        onClick={onClear}
      >
        <X className="h-3 w-3" />
        取消选择
      </button>
    </div>
  );
}

/**
 * Pick a non-thumbnail icon component for a file entry based on
 * extension. Keeps the icon palette stable in both list and grid
 * (placeholder) views so the user can quickly tell APK from doc from
 * archive at a glance.
 */
function fileIconFor(name: string): typeof FileIcon {
  switch (categoryOf(name)) {
    case 'image':
      return ImageIcon;
    case 'video':
      return Film;
    case 'audio':
      return FileAudio;
    case 'doc':
      return FileText;
    case 'apk':
      return Package;
    case 'archive':
      return FileArchive;
    default:
      return FileIcon;
  }
}

interface DeviceRowActions {
  entries: DeviceFsEntry[];
  selected: Set<string>;
  toggleSelected: (name: string) => void;
  setPath: (p: string) => void;
  currentPath: string;
  onPreview: (entry: DeviceFsEntry) => void;
  onPull: (entry: DeviceFsEntry) => void;
  onDelete: (entry: DeviceFsEntry) => void;
}

function DeviceListView({
  allSelected,
  toggleSelectAll,
  ...p
}: DeviceRowActions & {
  allSelected: boolean;
  toggleSelectAll: () => void;
}) {
  return (
    <ul>
      <li className="flex items-center gap-2 border-b border-line bg-bg-soft px-3 py-1 text-[11px] text-ink-muted">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          aria-label="全选当前可见文件"
        />
        <span>勾选可批量操作（仅文件，目录不计入）</span>
      </li>
      {p.entries.map((entry) => {
        const isDir = entry.type === 'dir' || entry.type === 'symlink';
        const previewable = !isDir && isPreviewableName(entry.name);
        const Icon = isDir ? Folder : fileIconFor(entry.name);
        const isChecked = p.selected.has(entry.name);
        return (
          <li
            key={entry.name}
            className={clsx(
              'flex items-center justify-between border-b border-line px-3 py-1.5 text-sm hover:bg-bg-soft',
              isChecked && 'bg-accent/5'
            )}
          >
            {!isDir ? (
              <input
                type="checkbox"
                className="mr-2"
                checked={isChecked}
                onChange={() => p.toggleSelected(entry.name)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`选择 ${entry.name}`}
              />
            ) : (
              <span className="mr-2 inline-block w-4" />
            )}
            <button
              className="flex flex-1 items-center gap-2 text-left"
              onClick={() => {
                if (isDir) p.setPath(p.currentPath.replace(/\/?$/, '/') + entry.name + '/');
                else if (previewable) p.onPreview(entry);
              }}
              title={previewable ? '点击预览（设备直读，不写 PC 磁盘）' : entry.name}
            >
              <Icon
                className={clsx(
                  'h-4 w-4 shrink-0',
                  isDir ? 'text-amber-500' : 'text-ink-dim'
                )}
              />
              <span
                className={clsx(
                  'truncate font-mono text-xs',
                  previewable && 'text-ink hover:text-accent'
                )}
              >
                {entry.name}
              </span>
              <span className="ml-auto text-[11px] text-ink-muted">{entry.permissions}</span>
            </button>
            <span className="ml-3 w-20 text-right text-[11px] tabular-nums text-ink-muted">
              {formatBytes(entry.size)}
            </span>
            {previewable && (
              <button
                className="btn-ghost"
                title="预览（设备直读，不落 PC 磁盘）"
                onClick={(e) => {
                  e.stopPropagation();
                  p.onPreview(entry);
                }}
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            )}
            {!isDir && (
              <button
                className="btn-ghost"
                title="拉到 PC"
                onClick={(e) => {
                  e.stopPropagation();
                  p.onPull(entry);
                }}
              >
                <Download className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              className="btn-ghost text-rose-500"
              title="删除"
              onClick={(e) => {
                e.stopPropagation();
                p.onDelete(entry);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function DeviceGridView({
  serial,
  entries,
  currentPath,
  selected,
  toggleSelected,
  setPath,
  onPreview,
  onPull,
  onDelete,
}: {
  serial: string;
  entries: DeviceFsEntry[];
  currentPath: string;
  selected: Set<string>;
  toggleSelected: (name: string) => void;
  setPath: (p: string) => void;
  onPreview: (entry: DeviceFsEntry) => void;
  onPull: (entry: DeviceFsEntry) => void;
  onDelete: (entry: DeviceFsEntry) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {entries.map((entry) => {
        const isDir = entry.type === 'dir' || entry.type === 'symlink';
        const previewable = !isDir && isPreviewableName(entry.name);
        const isChecked = !isDir && selected.has(entry.name);
        const fullPath = currentPath.replace(/\/?$/, '/') + entry.name;
        return (
          <div
            key={entry.name}
            className={clsx(
              'group relative flex flex-col overflow-hidden rounded-md border bg-bg-card',
              isChecked ? 'border-accent ring-2 ring-accent/40' : 'border-line'
            )}
          >
            <button
              type="button"
              className="block aspect-square w-full bg-bg-soft text-left"
              onClick={() => {
                if (isDir) setPath(fullPath + '/');
                else if (previewable) onPreview(entry);
              }}
              title={entry.name}
            >
              {isDir ? (
                <div className="flex h-full w-full items-center justify-center">
                  <Folder className="h-10 w-10 text-amber-500" />
                </div>
              ) : (
                <Thumb serial={serial} path={fullPath} name={entry.name} />
              )}
            </button>
            {!isDir && (
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleSelected(entry.name)}
                onClick={(e) => e.stopPropagation()}
                className={clsx(
                  'absolute left-1.5 top-1.5 cursor-pointer',
                  !isChecked && 'opacity-0 group-hover:opacity-100'
                )}
                aria-label={`选择 ${entry.name}`}
              />
            )}
            <div className="flex items-center justify-between gap-1 border-t border-line px-2 py-1 text-[11px]">
              <span className="truncate font-mono" title={entry.name}>
                {entry.name}
              </span>
              <span className="shrink-0 tabular-nums text-ink-muted">
                {formatBytes(entry.size)}
              </span>
            </div>
            <div className="flex items-center justify-end gap-1 border-t border-line bg-bg-soft px-1 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {previewable && (
                <button
                  className="btn-ghost"
                  title="预览"
                  onClick={() => onPreview(entry)}
                >
                  <Eye className="h-3 w-3" />
                </button>
              )}
              {!isDir && (
                <button
                  className="btn-ghost"
                  title="拉到 PC"
                  onClick={() => onPull(entry)}
                >
                  <Download className="h-3 w-3" />
                </button>
              )}
              <button
                className="btn-ghost text-rose-500"
                title="删除"
                onClick={() => onDelete(entry)}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Grid thumbnail with IntersectionObserver-gated `src`. Until the card
 * scrolls anywhere near the viewport we keep the file-type icon
 * rendered and skip the `deviceFsApi.rawUrl` fetch entirely — otherwise
 * opening a folder with 200 photos would kick off 200 concurrent
 * `adb exec-out cat` streams the moment the grid mounts.
 */
function Thumb({
  serial,
  path,
  name,
}: {
  serial: string;
  path: string;
  name: string;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (visible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  const Icon = fileIconFor(name);
  const src = visible && !errored ? deviceFsApi.rawUrl(serial, path) : undefined;

  if (isImageName(name)) {
    return (
      <div ref={ref} className="flex h-full w-full items-center justify-center">
        {src ? (
          <img
            src={src}
            alt={name}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <Icon className="h-10 w-10 text-ink-dim" />
        )}
      </div>
    );
  }
  if (isVideoName(name)) {
    return (
      <div ref={ref} className="flex h-full w-full items-center justify-center">
        {src ? (
          // preload="metadata" gets us the first frame as a poster
          // without streaming the whole file. Some ROM-side codecs
          // refuse this and the browser falls back to a black square,
          // which is fine — the file icon already labels the row.
          <video
            src={src}
            preload="metadata"
            muted
            playsInline
            className="h-full w-full object-cover"
            onError={() => setErrored(true)}
          />
        ) : (
          <Icon className="h-10 w-10 text-ink-dim" />
        )}
      </div>
    );
  }
  return (
    <div ref={ref} className="flex h-full w-full items-center justify-center">
      <Icon className="h-10 w-10 text-ink-dim" />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/* PC pane                                                              */
/* -------------------------------------------------------------------- */

interface PcPaneProps {
  serial: string | undefined;
  kind: PcTabKind;
  setKind: (k: PcTabKind) => void;
  items: FileMeta[];
  loading: boolean;
  refetch: () => void;
  onPushFile: (file: FileMeta) => void;
  onPreview: (file: FileMeta) => void;
  onDelete: (paths: string[]) => void;
  canPushToDevice: boolean;
  /** Resolved devices root from /api/server-info (for header/footer hints). */
  devicesRoot?: string;
  /** User home dir; used to render `~/...` instead of `/Users/foo/...`. */
  homeDir?: string;
  /** See `DevicePaneProps` for the fold-control contract. */
  folded?: boolean;
  onFold?: () => void;
  siblingFolded?: boolean;
  onUnfoldSibling?: () => void;
}

type AllSortKey = 'kind' | 'name' | 'bytes' | 'mtime';

function PcPane({
  serial,
  kind,
  setKind,
  items,
  loading,
  refetch,
  onPushFile,
  onPreview,
  onDelete,
  canPushToDevice,
  devicesRoot,
  homeDir,
  onFold,
  siblingFolded,
  onUnfoldSibling,
}: PcPaneProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Flat-table state — only meaningful when `kind === 'all'`. Lifted to
  // PcPane so a quick tab flip doesn't reset the user's sort + filter.
  const [allSort, setAllSort] = useState<{ key: AllSortKey; dir: 'asc' | 'desc' }>({
    key: 'mtime',
    dir: 'desc',
  });
  const [allKindFilter, setAllKindFilter] = useState<Set<DeviceFileKind>>(
    () => new Set(['screenshots', 'recordings', 'logcat', 'apks'])
  );

  const toggle = (p: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  // Visible rows after kind filtering (only applies in `all` mode); also
  // sorted by the active column. For single-kind tabs the original
  // order from the server is preserved (it's already mtime-desc).
  const visible = useMemo(() => {
    if (kind !== 'all') return items;
    const filtered = items.filter((it) => allKindFilter.has(it.kind));
    const dir = allSort.dir === 'asc' ? 1 : -1;
    const out = [...filtered];
    out.sort((a, b) => {
      let cmp = 0;
      switch (allSort.key) {
        case 'kind':
          cmp = a.kind.localeCompare(b.kind);
          break;
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'bytes':
          cmp = a.bytes - b.bytes;
          break;
        case 'mtime':
          cmp = a.mtime.localeCompare(b.mtime);
          break;
      }
      return dir * cmp;
    });
    return out;
  }, [items, kind, allKindFilter, allSort]);

  const allSelected = selected.size > 0 && selected.size === visible.length;
  const toggleAll = (): void => {
    setSelected(allSelected ? new Set() : new Set(visible.map((i) => i.path)));
  };

  const onSortClick = (key: AllSortKey): void => {
    setAllSort((cur) =>
      cur.key === key
        ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: key === 'name' || key === 'kind' ? 'asc' : 'desc' }
    );
  };

  const tabs: PcTabKind[] = ['all', 'screenshots', 'recordings', 'logcat', 'apks'];

  return (
    <section className="card flex flex-col overflow-hidden">
      <header className="flex items-center justify-between border-b border-line bg-bg-soft px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          {siblingFolded && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-accent/40 bg-accent/10 px-1.5 py-0.5 text-[11px] text-accent hover:bg-accent/20"
              onClick={onUnfoldSibling}
              title="展开左侧手机"
            >
              <PanelLeftOpen className="h-3.5 w-3.5" />
              <span>展开左侧</span>
            </button>
          )}
          {devicesRoot ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded border border-transparent px-1.5 py-0.5 text-xs text-ink-muted hover:border-line hover:bg-bg-card hover:text-ink"
              title={`在文件管理器中打开设备文件目录: ${devicesRoot}`}
              onClick={() => void revealInOsFileManager(devicesRoot)}
            >
              <ExternalLink className="h-3 w-3" />
              <span>PC 本地存档</span>
            </button>
          ) : (
            <span className="text-xs text-ink-muted">PC 本地存档</span>
          )}
          <div className="inline-flex rounded-md border border-line bg-bg-card p-0.5">
            {tabs.map((k) => (
              <button
                key={k}
                className={clsx(
                  'px-2 py-0.5 text-xs',
                  kind === k ? 'rounded bg-accent text-white' : 'text-ink-muted'
                )}
                onClick={() => {
                  setKind(k);
                  setSelected(new Set());
                }}
              >
                {PC_KIND_LABEL[k]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {selected.size > 0 && (
            <button
              className="btn"
              onClick={() => {
                onDelete([...selected]);
                setSelected(new Set());
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>删除 {selected.size}</span>
            </button>
          )}
          <button className="btn-ghost" onClick={refetch} disabled={!serial}>
            <RotateCcw className="h-3.5 w-3.5" />
          </button>
          {onFold && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-card px-1.5 py-0.5 text-[11px] text-ink-muted hover:border-accent/40 hover:bg-accent/10 hover:text-accent"
              onClick={onFold}
              title="折叠 PC 侧，让手机占满"
            >
              <PanelRightClose className="h-3.5 w-3.5" />
              <span>折叠</span>
            </button>
          )}
        </div>
      </header>

      {/* Kind filter chips only on the 全部 tab. The chip row sits above
          the table so kind toggles affect the visible rows immediately. */}
      {kind === 'all' && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-bg-soft/60 px-3 py-1.5 text-[11px]">
          <span className="text-ink-dim">类型</span>
          {(['screenshots', 'recordings', 'logcat', 'apks'] as DeviceFileKind[]).map((k) => {
            const on = allKindFilter.has(k);
            return (
              <button
                key={k}
                type="button"
                className={clsx(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition',
                  on
                    ? 'border-accent/60 bg-accent/10 text-accent'
                    : 'border-line text-ink-muted hover:bg-bg-soft'
                )}
                onClick={() =>
                  setAllKindFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(k)) next.delete(k);
                    else next.add(k);
                    return next;
                  })
                }
              >
                <span className={clsx('h-1.5 w-1.5 rounded-full', kindDotClass(k))} />
                {PC_KIND_LABEL[k]}
              </button>
            );
          })}
          <button
            type="button"
            className="ml-auto text-ink-dim hover:text-ink"
            onClick={() =>
              setAllKindFilter(new Set(['screenshots', 'recordings', 'logcat', 'apks']))
            }
          >
            重置
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto">
        {loading && <p className="px-3 py-3 text-xs text-ink-muted">加载中…</p>}

        {/* Header row (single-kind list keeps the original lightweight
            row count; 全部 view gets a sortable column header). */}
        {visible.length > 0 && kind !== 'all' && (
          <div className="flex items-center gap-2 border-b border-line bg-bg-soft px-3 py-1.5 text-[11px] text-ink-muted">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            <span>
              共 {visible.length} 个文件
              {selected.size > 0 && ` · 已选 ${selected.size}`}
            </span>
          </div>
        )}

        {visible.length > 0 && kind === 'all' && (
          <div className="grid grid-cols-[24px_64px_minmax(0,1fr)_72px_148px_104px] items-center gap-2 border-b border-line bg-bg-soft px-3 py-1.5 text-[11px] text-ink-muted">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            <SortBtn label="类型" k="kind" sort={allSort} onClick={onSortClick} />
            <SortBtn label="文件名" k="name" sort={allSort} onClick={onSortClick} />
            <SortBtn
              label="大小"
              k="bytes"
              sort={allSort}
              onClick={onSortClick}
              align="right"
            />
            <SortBtn label="时间" k="mtime" sort={allSort} onClick={onSortClick} />
            <span className="text-right text-ink-dim">操作</span>
          </div>
        )}

        <ul>
          {visible.map((f) => {
            const checked = selected.has(f.path);
            if (kind === 'all') {
              return (
                <li
                  key={f.path}
                  className={clsx(
                    'grid grid-cols-[24px_64px_minmax(0,1fr)_72px_148px_104px] items-center gap-2 border-b border-line/60 px-3 py-1.5 text-sm hover:bg-bg-soft',
                    checked && 'bg-bg-soft/60'
                  )}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(f.path)} />
                  <KindBadge kind={f.kind} />
                  <button
                    className="flex min-w-0 items-center gap-2 text-left"
                    onClick={() => onPreview(f)}
                    title={f.path}
                  >
                    <span className="truncate font-mono text-xs text-ink hover:text-accent">
                      {f.name}
                    </span>
                  </button>
                  <span className="text-right text-[11px] tabular-nums text-ink-muted">
                    {formatBytes(f.bytes)}
                  </span>
                  <span className="font-mono text-[11px] tabular-nums text-ink-muted">
                    {formatMtime(f.mtime)}
                  </span>
                  <div className="flex justify-end gap-0.5">
                    <button
                      className="btn-ghost"
                      title="推送到设备"
                      onClick={() => onPushFile(f)}
                      disabled={!canPushToDevice}
                    >
                      <Upload className="h-3.5 w-3.5" />
                    </button>
                    <a
                      className="btn-ghost"
                      href={filesApi.rawUrl(f.path, true)}
                      title="下载到本机浏览器"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button
                      className="btn-ghost"
                      title="在文件管理器中显示"
                      onClick={() => void revealInOsFileManager(f.path)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="btn-ghost text-rose-500"
                      title="删除"
                      onClick={() => onDelete([f.path])}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li
                key={f.path}
                className={clsx(
                  'flex items-center justify-between border-b border-line px-3 py-1.5 text-sm hover:bg-bg-soft',
                  checked && 'bg-bg-soft/60'
                )}
              >
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={checked}
                  onChange={() => toggle(f.path)}
                />
                <button
                  className="flex flex-1 items-center gap-2 text-left"
                  onClick={() => onPreview(f)}
                  title="点击预览"
                >
                  <span className="h-4 w-4 rounded bg-bg-soft text-center text-[10px] leading-4 text-ink-muted">
                    {kindGlyph(f.kind)}
                  </span>
                  <span className="font-mono text-xs text-ink hover:text-accent">{f.name}</span>
                </button>
                <span className="ml-3 w-20 text-right text-[11px] tabular-nums text-ink-muted">
                  {formatBytes(f.bytes)}
                </span>
                <button
                  className="btn-ghost"
                  title="推送到设备"
                  onClick={() => onPushFile(f)}
                  disabled={!canPushToDevice}
                >
                  <Upload className="h-3.5 w-3.5" />
                </button>
                <a
                  className="btn-ghost"
                  href={filesApi.rawUrl(f.path, true)}
                  title="下载到本机浏览器"
                >
                  <Download className="h-3.5 w-3.5" />
                </a>
                <button
                  className="btn-ghost"
                  title="在文件管理器中显示"
                  onClick={() => void revealInOsFileManager(f.path)}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
                <button
                  className="btn-ghost text-rose-500"
                  title="删除"
                  onClick={() => onDelete([f.path])}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            );
          })}
          {visible.length === 0 && !loading && (
            <li className="px-3 py-6 text-center text-xs text-ink-muted">
              {kind === 'all'
                ? items.length === 0
                  ? '还没有任何存档文件。'
                  : '当前筛选下没有匹配的文件。'
                : `${PC_KIND_LABEL[kind]} 还没有任何文件。`}
            </li>
          )}
        </ul>
      </div>

      <footer className="border-t border-line bg-bg-soft px-3 py-1.5 font-mono text-[11px] text-ink-muted">
        {devicesRoot
          ? `${humanisePath(devicesRoot, homeDir)}/${serial ?? '<serial>'}/${
              kind === 'all' ? '*' : kind
            }/`
          : `<devices>/${serial ?? '<serial>'}/${kind === 'all' ? '*' : kind}/`}
      </footer>
    </section>
  );
}

/**
 * Sortable column header used by the 全部 table. Keeps the active
 * column highlighted in accent and shows an up/down chevron for
 * the current direction.
 */
function SortBtn({
  label,
  k,
  sort,
  onClick,
  align = 'left',
}: {
  label: string;
  k: AllSortKey;
  sort: { key: AllSortKey; dir: 'asc' | 'desc' };
  onClick: (k: AllSortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === k;
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex items-center gap-0.5 truncate hover:text-ink',
        active && 'text-accent',
        align === 'right' && 'justify-end'
      )}
      onClick={() => onClick(k)}
    >
      <span>{label}</span>
      {active &&
        (sort.dir === 'asc' ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        ))}
    </button>
  );
}

/** Coloured pill rendering the file's DeviceFileKind in the 全部 view. */
function KindBadge({ kind }: { kind: DeviceFileKind }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium',
        KIND_BADGE[kind]
      )}
    >
      {PC_KIND_LABEL[kind]}
    </span>
  );
}

function kindDotClass(k: DeviceFileKind): string {
  switch (k) {
    case 'screenshots':
      return 'bg-cyan-500';
    case 'recordings':
      return 'bg-fuchsia-500';
    case 'logcat':
      return 'bg-slate-500';
    case 'apks':
      return 'bg-emerald-500';
  }
}

function kindGlyph(k: DeviceFileKind): string {
  if (k === 'screenshots') return 'i';
  if (k === 'recordings') return 'v';
  if (k === 'apks') return 'a';
  return 'l';
}

/**
 * Compact `YYYY-MM-DD HH:mm` rendering for the table's 时间 column.
 * Strips seconds (server returns ms-precision ISO timestamps) so
 * the column stays narrow without sacrificing useful info.
 */
function formatMtime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
